import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

// Times are exchanged as "HH:mm:ss" strings (C# TimeOnly). Dates as "yyyy-MM-dd".

export interface ShiftTemplateDto {
  shiftTemplateId: string;
  nameAr: string;
  nameEn: string | null;
  branchId: string | null;
  startTime: string;
  endTime: string;
  color: string | null;
  isActive: boolean;
  hours: number;
}

export interface ShiftTemplateInput {
  nameAr: string;
  nameEn?: string | null;
  branchId?: string | null;
  startTime: string;
  endTime: string;
  color?: string | null;
  isActive?: boolean;
}

export interface ShiftAssignmentDto {
  shiftAssignmentId: string;
  employeeId: string;
  shiftTemplateId: string | null;
  templateNameAr: string | null;
  templateNameEn: string | null;
  branchId: string | null;
  workDate: string;
  startTime: string;
  endTime: string;
  status: number; // 0 = Draft, 1 = Published
  notes: string | null;
  hours: number;
}

export interface RosterEmployeeDto {
  employeeId: string;
  nameAr: string;
  nameEn: string | null;
  positionAr: string;
  branchId: string | null;
  scheduledHours: number;
  estimatedCost: number;
}

export interface RosterWeekDto {
  from: string;
  to: string;
  employees: RosterEmployeeDto[];
  assignments: ShiftAssignmentDto[];
  templates: ShiftTemplateDto[];
  totalHours: number;
  totalEstimatedCost: number;
}

const TEMPLATES_QK = ['shift-templates'] as const;
const rosterQK = (from: string, to: string, branchId: string | null) =>
  ['roster', from, to, branchId ?? 'all'] as const;

// ---- Templates ----

export function useShiftTemplates() {
  return useQuery({
    queryKey: TEMPLATES_QK,
    queryFn: async () => (await apiClient.get<ShiftTemplateDto[]>('/api/hr/shift-templates')).data,
  });
}

export function useCreateShiftTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: ShiftTemplateInput) =>
      (await apiClient.post<ShiftTemplateDto>('/api/hr/shift-templates', req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_QK }),
  });
}

export function useUpdateShiftTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, req }: { id: string; req: ShiftTemplateInput }) =>
      (await apiClient.put<ShiftTemplateDto>(`/api/hr/shift-templates/${id}`, req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_QK }),
  });
}

export function useDeleteShiftTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/hr/shift-templates/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_QK }),
  });
}

// ---- Roster ----

export function useRosterWeek(from: string, to: string, branchId: string | null) {
  return useQuery({
    queryKey: rosterQK(from, to, branchId),
    queryFn: async () =>
      (
        await apiClient.get<RosterWeekDto>('/api/hr/roster', {
          params: { from, to, ...(branchId ? { branchId } : {}) },
        })
      ).data,
  });
}

export interface CreateAssignmentInput {
  employeeId: string;
  shiftTemplateId?: string | null;
  branchId?: string | null;
  workDate: string;
  startTime: string;
  endTime: string;
  notes?: string | null;
}

export interface UpdateAssignmentInput {
  shiftTemplateId?: string | null;
  branchId?: string | null;
  workDate: string;
  startTime: string;
  endTime: string;
  notes?: string | null;
}

function invalidateRoster(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ['roster'] });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CreateAssignmentInput) =>
      (await apiClient.post<ShiftAssignmentDto>('/api/hr/roster', req)).data,
    onSuccess: () => invalidateRoster(qc),
  });
}

export function useUpdateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, req }: { id: string; req: UpdateAssignmentInput }) =>
      (await apiClient.put<ShiftAssignmentDto>(`/api/hr/roster/${id}`, req)).data,
    onSuccess: () => invalidateRoster(qc),
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/hr/roster/${id}`);
    },
    onSuccess: () => invalidateRoster(qc),
  });
}

export function usePublishRoster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: { from: string; to: string }) =>
      (await apiClient.post<{ published: number }>('/api/hr/roster/publish', req)).data,
    onSuccess: () => invalidateRoster(qc),
  });
}
